import { Link } from "react-router-dom";
import type { Scoop } from "@/mocks/types";
import VerdictBadge from "@/components/base/VerdictBadge";

interface ScoopCardProps {
  scoop: Scoop;
  variant?: "card" | "row" | "feature";
}

export default function ScoopCard({ scoop, variant = "card" }: ScoopCardProps) {
  if (variant === "row") {
    return (
      <Link
        to={`/scoop/${scoop.id}`}
        className="group flex items-start gap-4 border-b border-background-200 py-4 transition-colors hover:bg-background-100/70 px-2 -mx-2 rounded-md"
      >
        <span className="font-label text-xs text-foreground-400 pt-1 whitespace-nowrap hidden sm:block">
          {scoop.date.slice(5)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <VerdictBadge verdict={scoop.verdict} size="sm" />
            <span className="text-[11px] text-foreground-500 whitespace-nowrap">{scoop.category}</span>
          </div>
          <h4 className="font-heading text-[15px] leading-snug text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
            {scoop.title}
          </h4>
          <div className="mt-1.5 flex items-center gap-3 text-[11px] text-foreground-500 font-label">
            <span className="whitespace-nowrap"><i className="ri-file-list-3-line mr-1"></i>{scoop.code}</span>
            <span className="whitespace-nowrap"><i className="ri-fire-line mr-1"></i>{scoop.heat}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "feature") {
    return (
      <Link
        to={`/scoop/${scoop.id}`}
        className="group grid grid-cols-1 md:grid-cols-2 overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
      >
        <div className="relative w-full h-56 md:h-full min-h-[240px] overflow-hidden">
          <img
            src={scoop.cover}
            alt={`${scoop.title} 的证据材料整理配图`}
            title={`${scoop.title} · 拾真社独家爆料求证档案`}
            className="absolute inset-0 h-full w-full object-top object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <span className="absolute left-3 top-3 rounded-md bg-foreground-950/80 px-2.5 py-1 font-label text-[11px] text-background-50 backdrop-blur">
            {scoop.code}
          </span>
        </div>
        <div className="flex flex-col justify-center p-5 md:p-7">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <VerdictBadge verdict={scoop.verdict} />
            <span className="text-xs text-foreground-500">{scoop.category}</span>
          </div>
          <h3 className="font-heading text-xl md:text-2xl font-bold leading-snug text-foreground-950 group-hover:text-primary-600 transition-colors">
            {scoop.title}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 line-clamp-3">{scoop.summary}</p>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-label text-[11px] text-foreground-500">
            <span className="whitespace-nowrap"><i className="ri-links-line mr-1"></i>{scoop.sources} 个来源</span>
            <span className="whitespace-nowrap"><i className="ri-fire-line mr-1"></i>热度 {scoop.heat}</span>
            <span className="whitespace-nowrap"><i className="ri-time-line mr-1"></i>{scoop.readMinutes} 分钟</span>
            <span className="whitespace-nowrap">更新 {scoop.updated.slice(5, 10)}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:border-primary-300 hover:-translate-y-0.5">
      <Link to={`/scoop/${scoop.id}`} className="relative block w-full h-48 overflow-hidden">
        <img
          src={scoop.cover}
          alt={`${scoop.title} 的档案配图`}
          title={`${scoop.title} · 拾真社求证档案`}
          className="absolute inset-0 h-full w-full object-top object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3">
          <VerdictBadge verdict={scoop.verdict} size="sm" className="!px-2 !py-1" />
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-label text-[11px] text-foreground-500">
          <span className="whitespace-nowrap">{scoop.code}</span>
          <span className="whitespace-nowrap text-primary-600">{scoop.category}</span>
        </div>
        <h3 className="font-heading text-base font-bold leading-snug text-foreground-950">
          <Link to={`/scoop/${scoop.id}`} className="hover:text-primary-600 transition-colors">
            {scoop.title}
          </Link>
        </h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600 line-clamp-3">{scoop.summary}</p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 font-label text-[11px] text-foreground-500">
          <span className="whitespace-nowrap"><i className="ri-links-line mr-1"></i>{scoop.sources} 来源</span>
          <span className="whitespace-nowrap">可信度 {scoop.credibility}</span>
        </div>
      </div>
    </article>
  );
}